import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Button,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Removed axios import to use native fetch
import apiConfig from "../config";

const QuoteModal = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    serviceInterest: "Interior Design Consultation",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${apiConfig.apiUrl}/forms/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.status) {
        setSnackbar({
          open: true,
          message:
            "Thanks for contacting us! Our team will get back to you shortly.",
          severity: "success",
        });

        setTimeout(() => {
          onClose();
          setFormData({
            fullName: "",
            email: "",
            phoneNumber: "",
            serviceInterest: "Interior Design Consultation",
            message: "",
          });
        }, 2000);
      } else {
        throw new Error(data.message || "Submission failed");
      }
    } catch (error) {
      console.error("Submission error:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to submit enquiry. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        disableScrollLock={true}
        SlotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(28, 25, 23, 0.4)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
            },
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: "24px",
            p: { xs: 1, sm: 2 },
            margin: { xs: "16px", sm: "32px" },
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ pt: 0 }}>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            {/* Name and Email Row - Stack on mobile, side-by-side on desktop */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                width: "100%",
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontWeight: 500,
                    fontSize: "12px",
                    color: "#000000ff",
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                    mb: 1,
                  }}
                >
                  Full Name <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  placeholder="John Doe"
                  variant="outlined"
                  value={formData.fullName}
                  onChange={handleChange("fullName")}
                  required
                  sx={{
                    backgroundColor: "#F5F5F4",
                    borderRadius: "4px",
                    "& .MuiOutlinedInput-root": {
                      height: "45px",
                      fontFamily: "Inter",
                      fontSize: "14px",
                      "& fieldset": { borderColor: "#E7E5E4" },
                      "&:hover fieldset": { borderColor: "#D6D3D1" },
                      "&.Mui-focused fieldset": { borderColor: "#B02F00" },
                    },
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontWeight: 500,
                    fontSize: "12px",
                    color: "#000000ff",
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                    mb: 1,
                  }}
                >
                  Email Address <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  placeholder="john@example.com"
                  variant="outlined"
                  type="email"
                  value={formData.email}
                  onChange={handleChange("email")}
                  required
                  sx={{
                    backgroundColor: "#F5F5F4",
                    borderRadius: "4px",
                    "& .MuiOutlinedInput-root": {
                      height: "45px",
                      fontFamily: "Inter",
                      fontSize: "14px",
                      "& fieldset": { borderColor: "#E7E5E4" },
                      "&:hover fieldset": { borderColor: "#D6D3D1" },
                      "&.Mui-focused fieldset": { borderColor: "#B02F00" },
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Service Interest Dropdown */}
            <Box>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 500,
                  fontSize: "12px",
                  color: "#000000ff",
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  mb: 1,
                }}
              >
                Service Interest <span style={{ color: "red" }}>*</span>
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={formData.serviceInterest}
                  onChange={handleChange("serviceInterest")}
                  sx={{
                    height: "45px",
                    backgroundColor: "#F5F5F4",
                    borderRadius: "4px",
                    fontFamily: "Inter",
                    fontSize: "14px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E7E5E4",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D6D3D1",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#B02F00",
                    },
                  }}
                >
                  <MenuItem value="Interior Design Consultation">
                    Interior Design Consultation
                  </MenuItem>
                  <MenuItem value="Construction Project">
                    Construction Project
                  </MenuItem>
                  <MenuItem value="Real Estate Inquiry">
                    Real Estate Inquiry
                  </MenuItem>
                  <MenuItem value="Other Services">Other Services</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Phone Field */}
            <Box>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 500,
                  fontSize: "12px",
                  color: "#000000ff",
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  mb: 1,
                }}
              >
                Phone Number <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="+91 1234567890"
                variant="outlined"
                value={formData.phoneNumber}
                onChange={handleChange("phoneNumber")}
                sx={{
                  backgroundColor: "#F5F5F4",
                  borderRadius: "4px",
                  "& .MuiOutlinedInput-root": {
                    height: "45px",
                    fontFamily: "Inter",
                    fontSize: "14px",
                    "& fieldset": { borderColor: "#E7E5E4" },
                    "&:hover fieldset": { borderColor: "#D6D3D1" },
                    "&.Mui-focused fieldset": { borderColor: "#B02F00" },
                  },
                }}
              />
            </Box>

            {/* Message Box */}
            <Box>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 500,
                  fontSize: "12px",
                  color: "#000000ff",
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  mb: 1,
                }}
              >
                Your Message
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Tell us about your project..."
                variant="outlined"
                value={formData.message}
                onChange={handleChange("message")}
                required
                sx={{
                  backgroundColor: "#F5F5F4",
                  borderRadius: "4px",
                  "& .MuiOutlinedInput-root": {
                    fontFamily: "Inter",
                    fontSize: "14px",
                    "& fieldset": { borderColor: "#E7E5E4" },
                    "&:hover fieldset": { borderColor: "#D6D3D1" },
                    "&.Mui-focused fieldset": { borderColor: "#B02F00" },
                  },
                }}
              />
            </Box>

            {/* Submit Button */}
            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={loading}
              sx={{
                mt: 1,
                height: "56px",
                borderRadius: "8px",
                background: "linear-gradient(135deg, #B02F00 0%, #FF5722 100%)",
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: "16px",
                color: "#FFFFFF",
                textTransform: "none",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #9A2800 0%, #E64A19 100%)",
                },
                "&:disabled": {
                  background: "#ccc",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Get a Quote"
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default QuoteModal;
